import { useState, useEffect, useCallback } from 'react'
import { Box, Flex, VStack, Grid, Spinner } from '@chakra-ui/react'
import {
  LuBuilding2 as Building2,
  LuUsers as Users,
  LuTruck as TruckIcon,
  LuClipboardList as ClipboardList,
  LuShieldCheck as ShieldCheck,
} from 'react-icons/lu'
import { useNavigate, useParams } from 'react-router-dom'
import { Text, useT, toaster, ConfirmDialog } from '@minetrace/ui'
import { useFormColors, WizardSidebar } from '@minetrace/ui/forms'
import { transportistasService } from '../../api/services'
import { ApiError } from '../../api/client'
import { getErrorMessage } from '../../api/errorCodes'
import { useHasPermission } from '../../auth/PermissionContext'
import type { TransportistaResponse, CarrierStepApprovalEntryResponse, CarrierQuestionAnswerResponse } from '../../api/models/response/transportista'
import type { ObservationResponse, StepApprovalEntry, StepApprovalStatus } from '../../api/models/response/proveedor'
import ObservationBox from '../../components/ObservationBox'
import SustainabilitySurvey from '../../components/SustainabilitySurvey'

type TabType = 'info' | 'conductores' | 'vehiculos' | 'encuesta'
const TAB_ORDER: TabType[] = ['info', 'conductores', 'vehiculos', 'encuesta']
const TAB_STEP: Record<TabType, number> = { info: 1, conductores: 2, vehiculos: 3, encuesta: 4 }

export default function EvaluarTransportistaPage() {
  const c = useFormColors()
  const { t } = useT()
  const navigate = useNavigate()
  const { id } = useParams()
  const canHomologate = useHasPermission('carrier.homologate')

  const [activeTab, setActiveTab] = useState<TabType>('info')
  const [data, setData] = useState<TransportistaResponse | null>(null)
  const [stepApprovals, setStepApprovals] = useState<CarrierStepApprovalEntryResponse[]>([])
  const [questions, setQuestions] = useState<CarrierQuestionAnswerResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [approvingStep, setApprovingStep] = useState<number | null>(null)
  const [authorizing, setAuthorizing] = useState(false)
  const [confirmAuthorize, setConfirmAuthorize] = useState(false)

  const refresh = useCallback(async () => {
    if (!id) return
    const [d, sa] = await Promise.all([
      transportistasService.getById(id),
      transportistasService.getStepApprovals(id).catch(() => [] as CarrierStepApprovalEntryResponse[]),
    ])
    setData(d)
    setStepApprovals(sa)
  }, [id])

  useEffect(() => {
    if (!id) return
    let active = true
    setLoading(true)
    Promise.all([
      transportistasService.getById(id),
      transportistasService.getStepApprovals(id).catch(() => [] as CarrierStepApprovalEntryResponse[]),
      transportistasService.getCompliance(id).then((x) => x.questions).catch(() => [] as CarrierQuestionAnswerResponse[]),
    ])
      .then(([d, sa, qs]) => { if (active) { setData(d); setStepApprovals(sa); setQuestions(qs) } })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [id])

  const handleApproveStep = async (step: number) => {
    if (!id) return
    setApprovingStep(step)
    try {
      await transportistasService.approveStep(id, step)
      await refresh()
    } catch (e) {
      toaster.error({ description: e instanceof ApiError ? getErrorMessage(e.errorCode, e.message) : t('transportistas.evaluar.error') })
    } finally {
      setApprovingStep(null)
    }
  }

  const handleAddObservation = async (text: string) => {
    if (!id) return
    try {
      await transportistasService.addObservation(id, TAB_STEP[activeTab], text)
      await refresh()
    } catch (e) {
      toaster.error({ description: e instanceof ApiError ? getErrorMessage(e.errorCode, e.message) : t('transportistas.evaluar.error') })
    }
  }

  const handleResolveObservation = async (obsId: string) => {
    if (!id) return
    try {
      await transportistasService.resolveObservation(id, obsId)
      await refresh()
    } catch (e) {
      toaster.error({ description: e instanceof ApiError ? getErrorMessage(e.errorCode, e.message) : t('transportistas.evaluar.error') })
    }
  }

  const handleAuthorize = async () => {
    if (!id) return
    setAuthorizing(true)
    try {
      await transportistasService.homologate(id)
      toaster.success({ description: t('transportistas.evaluar.approved') })
      setTimeout(() => navigate('/app/transportistas'), 1200)
    } catch (e) {
      toaster.error({ description: e instanceof ApiError ? getErrorMessage(e.errorCode, e.message) : t('transportistas.evaluar.error') })
    } finally {
      setAuthorizing(false)
    }
  }

  // Adaptadores al tipo que espera el ObservationBox compartido.
  const observations: ObservationResponse[] = (data?.observations ?? []).map((o) => ({
    id: o.id,
    step: o.step,
    observationText: o.observationText,
    createdByUserId: '',
    createdByUserName: null,
    resolvedAt: o.resolvedAt,
    createdAt: o.createdAt,
  }))

  // Hilos de respuesta por observación (estilo PR), para el ObservationBox.
  const repliesByObs: Record<string, { id: string; replyText: string; createdByUserName: string | null; createdAt: string }[]> = {}
  for (const o of data?.observations ?? []) {
    repliesByObs[o.id] = (o.replies ?? []).map((r) => ({ id: r.id, replyText: r.replyText, createdByUserName: r.createdByUserName, createdAt: r.createdAt }))
  }

  const handleObsReply = async (observationId: string, text: string) => {
    if (!id) return
    try {
      await transportistasService.addObservationReply(id, observationId, text)
      await refresh()
    } catch (e) {
      toaster.error({ description: e instanceof ApiError ? getErrorMessage(e.errorCode, e.message) : t('transportistas.evaluar.error') })
    }
  }

  const stepEntry = (step: number): StepApprovalEntry | null => {
    const e = stepApprovals.find((x) => x.step === step)
    if (!e) return null
    return {
      step: e.step,
      status: e.status as StepApprovalStatus,
      approvedAt: e.approvedAt,
      approvedByUserId: e.approvedByUserId,
      approvedByUsername: e.approvedByUsername,
    }
  }

  const obsPendingByStep = (step: number) => observations.filter((o) => o.step === step && !o.resolvedAt).length
  const sidebarStatus = (step: number): 'approved' | 'observed' | 'inReview' | 'pending' => {
    const entry = stepApprovals.find((x) => x.step === step)
    if (entry?.status === 'approved') return 'approved'
    if (obsPendingByStep(step) > 0) return 'observed'
    if (entry?.status === 'blockedByObservation') return 'inReview'
    return 'pending'
  }

  const allApproved = stepApprovals.length === TAB_ORDER.length && stepApprovals.every((e) => e.status === 'approved')
  const inReview = data ? data.registrationStatus === 1 || data.registrationStatus === 2 : false
  const isApproved = data?.registrationStatus === 3


  if (loading || !data) {
    return (
      <Flex justifyContent="center" py={16}><Spinner /></Flex>
    )
  }

  const tabs = [
    { key: 'info' as const, label: t('transportistas.evaluar.tabs.info'), icon: <Building2 size={18} />, status: sidebarStatus(1), observationCount: obsPendingByStep(1) },
    { key: 'conductores' as const, label: t('transportistas.evaluar.tabs.conductores'), icon: <Users size={18} />, status: sidebarStatus(2), observationCount: obsPendingByStep(2) },
    { key: 'vehiculos' as const, label: t('transportistas.evaluar.tabs.vehiculos'), icon: <TruckIcon size={18} />, status: sidebarStatus(3), observationCount: obsPendingByStep(3) },
    { key: 'encuesta' as const, label: t('transportistas.evaluar.tabs.encuesta'), icon: <ClipboardList size={18} />, status: sidebarStatus(4), observationCount: obsPendingByStep(4) },
  ]

  const field = (label: string, value: string | number | null | undefined) => (
    <Box>
      <Text fontSize="xs" color={c.textMuted} fontWeight="600" textTransform="uppercase" letterSpacing="0.04em">{label}</Text>
      <Text fontSize="sm" color={c.text} mt={0.5}>{value === null || value === undefined || value === '' ? '—' : value}</Text>
    </Box>
  )

  const renderInfoTab = () => (
    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={5}>
      {field(t('transportistas.nuevo.fields.ruc'), data.ruc)}
      {field(t('transportistas.nuevo.fields.legalName'), data.legalName)}
      {field(t('transportistas.nuevo.fields.commercialName'), data.commercialName)}
      {field(t('transportistas.nuevo.fields.taxAddress'), data.taxAddress)}
      {field(t('transportistas.nuevo.fields.email'), data.mainEmail)}
      {field(t('transportistas.nuevo.fields.phone'), data.mainPhone)}
      {field(t('transportistas.nuevo.fields.taxpayerStatus'), data.taxpayerStatus)}
      {field(t('transportistas.nuevo.fields.taxpayerCondition'), data.taxpayerCondition)}
      {field(t('transportistas.nuevo.fields.registrationDate'), data.registrationDate)}
    </Grid>
  )

  const renderConductoresTab = () => (
    (data.drivers ?? []).length === 0
      ? <Box p={6} textAlign="center" bg={c.bgHover} borderRadius="xl"><Text fontSize="sm" color={c.textMuted}>{t('transportistas.evaluar.empty.conductores')}</Text></Box>
      : <VStack gap={3} alignItems="stretch">
          {(data.drivers ?? []).map((d) => (
            <Box key={d.id} p={4} bg={c.bgHover} borderRadius="xl">
              <Text fontSize="sm" fontWeight="600" color={c.text}>{d.fullName}</Text>
              <Text fontSize="xs" color={c.textMuted}>
                {[d.identityDoc, d.licenseNumber ? `Lic: ${d.licenseNumber}` : '', d.licenseCategory ? `Cat: ${d.licenseCategory}` : ''].filter(Boolean).join(' | ')}
              </Text>
            </Box>
          ))}
        </VStack>
  )

  const renderVehiculosTab = () => (
    (data.trucks ?? []).length === 0
      ? <Box p={6} textAlign="center" bg={c.bgHover} borderRadius="xl"><Text fontSize="sm" color={c.textMuted}>{t('transportistas.evaluar.empty.vehiculos')}</Text></Box>
      : <VStack gap={3} alignItems="stretch">
          {(data.trucks ?? []).map((tr) => (
            <Box key={tr.id} p={4} bg={c.bgHover} borderRadius="xl">
              <Text fontSize="sm" fontWeight="600" color={c.text}>{tr.placa}</Text>
              <Text fontSize="xs" color={c.textMuted}>
                {[tr.marca, tr.modelo, tr.anio ? `(${tr.anio})` : ''].filter(Boolean).join(' ')}
                {tr.capacidadToneladas ? ` • ${tr.capacidadToneladas} TM` : ''}
              </Text>
            </Box>
          ))}
        </VStack>
  )

  const renderEncuestaTab = () => <SustainabilitySurvey questions={questions} readOnly />

  return (
    <>
      <Flex mx={-8} mt={-4}>
        <WizardSidebar
          title={t('transportistas.evaluar.title')}
          subtitle={data.legalName}
          steps={tabs}
          activeStep={activeTab}
          completedSteps={new Set(tabs.filter((tb) => tb.status === 'approved').map((tb) => tb.key))}
          onStepClick={(tab) => setActiveTab(tab)}
        />

        <Box flex={1} py={4} px={6} display="flex" flexDirection="column" minH="calc(100vh - 2rem)">
          {/* Header */}
          <Box mb={6}>
            <Box as="button" display="flex" alignItems="center" gap={1.5} color={c.textMuted} fontSize="sm" mb={1} cursor="pointer" _hover={{ color: c.text }} onClick={() => navigate(`/app/transportistas/${id}`)}>
              {t('transportistas.evaluar.back')}
            </Box>
            <Flex alignItems="center" gap={3}>
              <Text fontSize="2xl" fontWeight="800" color={c.text}>{data.legalName}</Text>
              <Text fontSize="xs" color={c.textMuted} fontFamily="monospace" bg={c.bgHover} px={2} py={0.5} borderRadius="md">{data.ruc}</Text>
            </Flex>
          </Box>

          {/* Aviso si aún no fue enviado a evaluación */}
          {!inReview && !isApproved && (
            <Box mb={5} p={4} borderRadius="xl" bg="rgba(61, 111, 168, 0.08)" border="1px solid" borderColor="rgba(61, 111, 168, 0.3)">
              <Text fontSize="sm" color={c.text}>{t('transportistas.evaluar.notInReview')}</Text>
            </Box>
          )}

          {/* Datos del paso (solo lectura) */}
          <Box mb={6}>
            {activeTab === 'info' && renderInfoTab()}
            {activeTab === 'conductores' && renderConductoresTab()}
            {activeTab === 'vehiculos' && renderVehiculosTab()}
            {activeTab === 'encuesta' && renderEncuestaTab()}
          </Box>

          {/* Caja de evaluación del paso (aprobar / observar / resolver) */}
          {canHomologate && inReview && (
            <ObservationBox
              step={TAB_STEP[activeTab]}
              observations={observations}
              approvalEntry={stepEntry(TAB_STEP[activeTab])}
              isApproving={approvingStep === TAB_STEP[activeTab]}
              onAdd={handleAddObservation}
              onResolve={handleResolveObservation}
              onApprove={() => handleApproveStep(TAB_STEP[activeTab])}
              repliesByObs={repliesByObs}
              onReply={handleObsReply}
            />
          )}

          {/* Botón final: aprobar transportista */}
          {inReview && (
            <Flex justifyContent="center" py={5}>
              <Box
                as="button" type="button" display="flex" alignItems="center" gap={2} px={8} h="48px"
                bg={allApproved ? '#1d6664' : '#b5aa9d'} color="white" borderRadius="xl" fontSize="md" fontWeight="700"
                cursor={allApproved && !authorizing && canHomologate ? 'pointer' : 'not-allowed'}
                opacity={authorizing ? 0.6 : 1}
                _hover={allApproved && canHomologate ? { bg: '#1d6664' } : {}}
                onClick={allApproved && !authorizing && canHomologate ? () => setConfirmAuthorize(true) : undefined}
                disabled={!allApproved || authorizing || !canHomologate}
              >
                <ShieldCheck size={18} />
                {authorizing ? t('transportistas.evaluar.approving') : t('transportistas.evaluar.approveButton')}
              </Box>
              {!allApproved && (
                <Text fontSize="xs" color="#a8740e" ml={3} alignSelf="center">{t('transportistas.evaluar.stepsPending')}</Text>
              )}
            </Flex>
          )}

          {isApproved && (
            <Box p={5} bg="#eef7f6" borderRadius="xl" border="1px solid" borderColor="#bfe3df" textAlign="center">
              <Text fontSize="lg" fontWeight="700" color="#1d6664">{t('transportistas.evaluar.alreadyApproved')}</Text>
            </Box>
          )}

        </Box>
      </Flex>

      <ConfirmDialog
        open={confirmAuthorize}
        onClose={() => { if (!authorizing) setConfirmAuthorize(false) }}
        onConfirm={async () => { await handleAuthorize(); setConfirmAuthorize(false) }}
        title={t('transportistas.evaluar.confirmApprove.title')}
        confirmLabel={t('transportistas.evaluar.confirmApprove.confirm')}
        cancelLabel={t('transportistas.evaluar.confirmApprove.cancel')}
        variant="default"
        isLoading={authorizing}
      >
        {t('transportistas.evaluar.confirmApprove.body')}
      </ConfirmDialog>
    </>
  )
}
